import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Modal, modalStyles } from '../../components/Modal';
import { ApiError, api } from '../../lib/api';
import { useToast } from '../../lib/toast';
import dashStyles from './Dashboard.module.css';

export function SetPasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');

  const save = useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post('/api/admin/set-password', body),
    onSuccess: () => {
      toast('Password updated');
      setCurrent('');
      setNewPassword('');
      setConfirm('');
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not update password'),
  });

  function submit(e?: React.FormEvent) {
    e?.preventDefault();
    setErr('');
    if (newPassword.length < 8) {
      setErr('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setErr('New password and confirmation do not match.');
      return;
    }
    save.mutate({ currentPassword, newPassword });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Set / change password"
      footer={
        <>
          <button className={modalStyles.ghost} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className={modalStyles.primary}
            onClick={() => submit()}
            type="button"
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save password'}
          </button>
        </>
      }
    >
      <form onSubmit={submit}>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
          Leave "Current password" blank if you have not set one yet.
        </p>
        {err && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        )}
        <div className={dashStyles.fgrid}>
          <div className={`${dashStyles.fitem} ${dashStyles.fitemFull}`}>
            <label>Current password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="(blank if you don't have one yet)"
            />
          </div>
          <div className={`${dashStyles.fitem} ${dashStyles.fitemFull}`}>
            <label>New password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
            />
          </div>
          <div className={`${dashStyles.fitem} ${dashStyles.fitemFull}`}>
            <label>Confirm new password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
